"use client";

import { useMemo } from "react";
import { type Columns, zipColumns } from "@/components/tools/example-columns";
import { StagedReflectionForm } from "@/components/tools/research-ops/staged-reflection-form";
import { useToolUi } from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const KOLB_REFLECTIVE_CYCLE_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  topicLabel: "What are you reflecting on?",
  topicPlaceholder: "e.g. Running my first peer-tutoring session",
  headingsLabel: "Give each stage its own heading",
  stagesHeading: "The four stages",
  stageFieldLabel: "{n}. {stage} — {question}",
  triggersHint: "Ask yourself: {questions}",
  wordCount: "one: word|other: words",
  fileName: "kolb-reflection",
  document: {
    heading: "Your reflection",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: stage still to write|other: stages still to write",
    complete: "All four stages are written.",
  },
  defaultTitle: "Reflective Account",
  titleWithTopic: "Reflection: {topic}",
  subtitle: "Structured with Kolb's experiential learning cycle (Kolb, 1984)",
  voiceNote:
    "Write in the first person — “I noticed”, “I realized” — because the subject is your own experience. Kolb's cycle is a loop, so end by saying what you will test next time and what you will look for when you do.",
  stageHeading: "{n}. {stage}",
  stagePrompt: "{question} {guide}",
  stagePromptNamed: "{stage} — {question} {guide}",
  stageNames: {
    experience: "Concrete experience",
    observation: "Reflective observation",
    conceptualization: "Abstract conceptualization",
    experimentation: "Active experimentation",
  },
  stageQuestions: {
    experience: "What happened?",
    observation:
      "What did you notice, and how does it look from other points of view?",
    conceptualization:
      "What ideas or theory explain it, and what do you conclude?",
    experimentation:
      "What will you try next, and how will you know whether it worked?",
  },
  stageGuides: {
    experience:
      "Describe one specific experience: where and when it was, who was there, what you did, and what happened. Stay factual — the interpretation comes in the next stages.",
    observation:
      "Step back and review it. What stood out, what surprised you, and where did what happened differ from what you expected? Include how others may have seen it.",
    conceptualization:
      "Make sense of it. Draw on theory, research, or professional guidance — and cite it — to explain why it went as it did, and state the general lesson you draw.",
    experimentation:
      "Turn the lesson into a plan: a specific change you will try in your next comparable situation, and what you will look for to judge whether it worked.",
  },
  referenceHeading: "Reference",
  referenceNote:
    "This is the APA 7 form of the reference. If you read a later printing or edition, or your course uses another style, cite the version and style you used.",
  references: {
    before: ["Kolb, D. A. (1984). "],
    title: [
      "Experiential learning: Experience as the source of learning and development",
    ],
    after: [". Prentice-Hall."],
  } as Columns<"before" | "title" | "after">,
  example: {
    topic: "Running my first peer-tutoring session",
    experience:
      "In week four I ran my first peer-tutoring session for six first-year statistics students. I had prepared a forty-minute walk-through of hypothesis testing and presented it from slides. After twenty minutes, two students had stopped taking notes and one asked whether we could just do practice questions.",
    observation:
      "Looking back, I did nearly all of the talking, and the only questions I asked were ones I answered myself. The students who stayed engaged were the two who had already attempted the problem set. The disengaged students were not unwilling; I think they could not tell what they didn't understand until they tried something. I had expected a lecture to feel helpful, but from their side it probably felt like watching.",
    conceptualization:
      "Kolb's own model explains part of this: learning needs the learner to do something with an idea, not only hear it — in his terms, experience has to be tested and reworked before it becomes knowledge. In my course reading on active learning, the consistent finding is that students who work on problems themselves outperform students who only listen. The general lesson I draw is that a tutoring session should be built around the students' attempts, with my explanations placed where they get stuck.",
    experimentation:
      "In my next session I will give students a problem in the first five minutes and explain only the step where most of them stall. I will ask each student to say what they tried before I answer. To judge whether it worked, I will count how many students are still working at the twenty-minute mark and ask each one at the end to name one thing they can now do that they could not at the start.",
  },
} as const;

const ORDER = [
  "experience",
  "observation",
  "conceptualization",
  "experimentation",
] as const;

type Strings = typeof KOLB_REFLECTIVE_CYCLE_GENERATOR_STRINGS;

/**
 * The strings as the staged-reflection builder wants them. `references` is stored as
 * columns (see `example-columns.ts`) and zipped back into rows here, English filling any
 * cell a translation left short.
 */
export function kolbLabels(strings: Strings) {
  return {
    ...strings,
    references: zipColumns(
      KOLB_REFLECTIVE_CYCLE_GENERATOR_STRINGS.references,
      strings.references
    ),
  };
}

export default function KolbReflectiveCycleGeneratorWidget() {
  const strings = useToolUi(KOLB_REFLECTIVE_CYCLE_GENERATOR_STRINGS);
  const labels = useMemo(() => kolbLabels(strings), [strings]);
  return (
    <StagedReflectionForm
      example={strings.example}
      form={strings}
      idPrefix="kolb"
      labels={labels}
      order={ORDER}
    />
  );
}
